// Comparing two of our own build-version strings.
//
// Every build we ship carries a version string produced by
// `scripts/git-version.js`, which runs `git describe --tags --always
// --abbrev=4`. That gives one of three shapes:
//
//   v1.31.8            a build sitting exactly on a release tag
//   v1.31.8-34-2c20a   a build 34 commits AFTER tag v1.31.8 (so: newer
//                      than that tag, NOT a pre-release of it)
//   2c20a1b            no tag was reachable at all, so `--always` fell
//                      back to a bare commit hash
//
// Server builds additionally get their environment appended by
// `server/deploy.js`, giving `v1.30.3-prod` or `v1.31.8-34-2c20a-dev`.
//
// These two helpers started life in the client's iOS in-app-update
// detector (`client/src/services/version-check.ios-lookup.ts`), which
// still re-exports them so its own callers are unchanged. They moved here
// so `tools/` can use them too — the ops CLI decides from them whether the
// server build that answered it is new enough (#4475; see
// `design-docs/subscription-ops-tooling.md`). `tools/` compiles
// `shared/src` directly but cannot reach `client/`.

// Strips a leading `v` and everything from the first `-` onward (the git
// build suffix) from a version string. `v1.24.1-7-3a14` -> `1.24.1`.
export function trimAppVersion(raw: string): string {
  const noV = raw.startsWith('v') ? raw.slice(1) : raw;
  const dash = noV.indexOf('-');
  return dash === -1 ? noV : noV.slice(0, dash);
}

// Three-part numeric semver compare. Returns -1, 0, or 1. Inputs are
// assumed already trimmed to `major.minor.patch`. We only need
// strict-greater-than on three-part versions, so this 12-line comparator
// stands in for the `semver` package and its dependency tree.
//
// CAUTION for new callers: the `|| 0` below means a string that is not a
// version at all — the bare-hash shape above, an empty body, an HTML error
// page — compares as `0.0.0` rather than raising. That is right for the
// client's use (a garbled App Store answer must not claim an update is
// available) but it is a trap anywhere a low answer triggers a refusal.
// Check the string is version-shaped BEFORE comparing if that is you;
// `tools/src/officer-rail.ts`'s `judgeServerBuild` is the worked example.
export function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map(n => parseInt(n, 10));
  const pb = b.split('.').map(n => parseInt(n, 10));
  for (let i = 0; i < 3; i++) {
    // `|| 0` coerces a missing part or a NaN to 0 so a short / dirty
    // string can't poison the compare with NaN (NaN comparisons are
    // always false and would silently read as "equal").
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x < y) return -1;
    if (x > y) return 1;
  }
  return 0;
}
